import { useCallback, useEffect, useState } from 'react';
import { findMoveById } from '@/data/moves';
import type { MoveRef, RecruitOutcome } from '@/schemas/character';
import type { ClassId } from '@/schemas/classId';
import { type ChoiceGroup, getMoveChoiceGroups, type Move } from '@/schemas/move';
import { isMoveChoiceAvailable, isMoveChoiceFree } from './moveChoiceConstraints';

/**
 * Custom hook che incapsula lo stato locale di setup mossa per
 * `SectionStartingMoves` (FI-18 Fase 2 + FI-32):
 *  - `choiceGroupsByMove`: mappa `moveId → groupId → selectedIds`
 *  - `narrativeFieldsByMove`: mappa `moveId → fieldId → testo`
 *  - sanitizzazione su cambio archetipo: rimuove opzioni con
 *    `requiresArchetype` non più valido (FI-32) e auto-aggiunge opzioni
 *    `freeForArchetype === archetypeId` non già presenti (FI-32). Copre sia
 *    `choices` shorthand (gruppo virtuale `'main'`) sia `choiceGroups`
 *    multi-gruppo.
 *  - 2 callback memoizzate per aggiornare il singolo gruppo/campo
 *
 * Estratto in file separato per rispettare `max-lines: 400` di
 * `SectionStartingMoves.tsx`.
 */
export function useMoveSetupState(
  initialMoves: readonly MoveRef[],
  classId: ClassId | undefined,
  archetypeId: string | undefined,
  selectedMoveIds: readonly string[],
) {
  const [choiceGroupsByMove, setChoiceGroupsByMove] = useState<
    Record<string, Record<string, string[]>>
  >(() =>
    sanitizeChoiceGroupsByArchetype(
      inferChoiceGroupsByMoveFromRefs(initialMoves, classId),
      classId,
      archetypeId,
      initialMoves.map((m) => m.id),
    ),
  );
  const [narrativeFieldsByMove, setNarrativeFieldsByMove] = useState<
    Record<string, Record<string, string>>
  >(() => inferNarrativeFieldsFromRefs(initialMoves));
  // D8a: esito del tiro al reclutamento per mosse-source di Follower con
  // `recruitRoll` (Druido Supporto Elementale, Sciamano Animare Zombi).
  const [recruitOutcomeByMove, setRecruitOutcomeByMove] = useState<Record<string, RecruitOutcome>>(
    () => inferRecruitOutcomeFromRefs(initialMoves),
  );

  // FI-32: sanitize su cambio archetipo o set di mosse selezionate. Copre
  // `requiresArchetype` (rimozione opzioni non più valide) e
  // `freeForArchetype` (auto-add opzioni gratis del nuovo archetipo). Lavora
  // su tutti i gruppi (shorthand `main` + multi-gruppo) e itera l'intero
  // catalogo delle mosse selezionate, anche se non ancora presenti nello
  // state locale (così le free vengono aggiunte la prima volta che il
  // gruppo viene visualizzato).
  useEffect(() => {
    setChoiceGroupsByMove((prev) =>
      sanitizeChoiceGroupsByArchetype(prev, classId, archetypeId, selectedMoveIds),
    );
  }, [classId, archetypeId, selectedMoveIds]);

  const handleChoiceGroupChange = useCallback(
    (moveId: string, groupId: string, selectedIds: string[]) =>
      setChoiceGroupsByMove((prev) => ({
        ...prev,
        [moveId]: { ...(prev[moveId] ?? {}), [groupId]: selectedIds },
      })),
    [],
  );

  const handleNarrativeFieldChange = useCallback(
    (moveId: string, fieldId: string, value: string) =>
      setNarrativeFieldsByMove((prev) => ({
        ...prev,
        [moveId]: { ...(prev[moveId] ?? {}), [fieldId]: value },
      })),
    [],
  );

  // D8a: cambio outcome resetta selezioni del gruppo `main` (il pickCount
  // cambia tra 10+ e 7-9 → le selezioni precedenti potrebbero violare il cap).
  const handleRecruitOutcomeChange = useCallback((moveId: string, outcome: RecruitOutcome) => {
    setRecruitOutcomeByMove((prev) => {
      if (prev[moveId] === outcome) return prev;
      return { ...prev, [moveId]: outcome };
    });
    setChoiceGroupsByMove((prev) => {
      const moveGroups = prev[moveId];
      if (!moveGroups?.main || moveGroups.main.length === 0) {
        return prev;
      }
      const { main: _resetMain, ...otherGroups } = moveGroups;
      return { ...prev, [moveId]: otherGroups };
    });
  }, []);

  return {
    choiceGroupsByMove,
    narrativeFieldsByMove,
    recruitOutcomeByMove,
    handleChoiceGroupChange,
    handleNarrativeFieldChange,
    handleRecruitOutcomeChange,
  };
}

function inferRecruitOutcomeFromRefs(moves: readonly MoveRef[]): Record<string, RecruitOutcome> {
  const result: Record<string, RecruitOutcome> = {};
  for (const m of moves) {
    if (m.recruitOutcome !== undefined) result[m.id] = m.recruitOutcome;
  }
  return result;
}

function inferChoiceGroupsByMoveFromRefs(
  moves: readonly MoveRef[],
  classId: ClassId | undefined,
): Record<string, Record<string, string[]>> {
  const result: Record<string, Record<string, string[]>> = {};
  for (const m of moves) {
    const move = classId ? findMoveById(classId, m.id) : undefined;
    const groups = collectGroupsForRef(m, move);
    if (Object.keys(groups).length > 0) result[m.id] = groups;
  }
  return result;
}

function collectGroupsForRef(ref: MoveRef, move: Move | undefined): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  if (move?.choices && ref.selectedChoices.length > 0) {
    groups.main = [...ref.selectedChoices];
  }
  if (move?.choiceGroups && ref.selectedChoiceGroups) {
    for (const [gid, ids] of Object.entries(ref.selectedChoiceGroups)) {
      groups[gid] = [...ids];
    }
  }
  return groups;
}

function inferNarrativeFieldsFromRefs(
  moves: readonly MoveRef[],
): Record<string, Record<string, string>> {
  const result: Record<string, Record<string, string>> = {};
  for (const m of moves) {
    if (m.narrativeFieldValues && Object.keys(m.narrativeFieldValues).length > 0) {
      result[m.id] = { ...m.narrativeFieldValues };
    }
  }
  return result;
}

/**
 * **FI-32**: sanitizza i `choiceGroupsByMove` rispetto all'archetipo
 * corrente. Per ogni mossa selezionata, per ogni gruppo:
 *  - rimuove opzioni con `requiresArchetype` non valide per `archetypeId`
 *  - auto-aggiunge le opzioni `freeForArchetype === archetypeId` non
 *    ancora presenti
 *
 * Ritorna la stessa istanza in input se nessuna modifica è stata
 * necessaria (referential equality, per evitare re-render).
 *
 * **Non rimuove** le opzioni con `freeForArchetype` impostato a un
 * archetipo DIVERSO dal corrente: tali opzioni sono comunque disponibili
 * a tutti (semplicemente non gratis), quindi una selezione "ex-free"
 * dopo un cambio archetipo resta valida come "paid". Effetto collaterale:
 * se l'utente cambia archetipo dopo aver compilato un gruppo, può ritrovarsi
 * il cap `pickCount` violato (e il predicate marcherà la sezione come
 * incompleta finché non rimuove manualmente l'opzione in eccesso). Edge
 * case accettato come trade-off di complessità.
 */
function sanitizeChoiceGroupsByArchetype(
  state: Record<string, Record<string, string[]>>,
  classId: ClassId | undefined,
  archetypeId: string | undefined,
  selectedMoveIds: readonly string[],
): Record<string, Record<string, string[]>> {
  if (!classId) return state;
  let changed = false;
  const next: Record<string, Record<string, string[]>> = {};
  // Itera prima sulle mosse correnti del draft (anche se non in state) per
  // poter applicare le free auto-add la prima volta che il gruppo viene
  // visualizzato. Poi preserva eventuali entry "orfane" in state (mosse non
  // più selezionate — caso edge che il parent ripulisce comunque al prossimo
  // SET_MOVES).
  const seenMoveIds = new Set<string>();
  for (const moveId of selectedMoveIds) {
    seenMoveIds.add(moveId);
    const result = processMoveSanitization(state, classId, archetypeId, moveId);
    if (result.changed) changed = true;
    if (result.groups) next[moveId] = result.groups;
  }
  for (const [moveId, groups] of Object.entries(state)) {
    if (seenMoveIds.has(moveId)) continue;
    next[moveId] = groups; // preserva entry orfane
  }
  return changed ? next : state;
}

function processMoveSanitization(
  state: Record<string, Record<string, string[]>>,
  classId: ClassId,
  archetypeId: string | undefined,
  moveId: string,
): { changed: boolean; groups: Record<string, string[]> | undefined } {
  const move = findMoveById(classId, moveId);
  const currentGroups = state[moveId];
  if (!move) {
    return { changed: false, groups: currentGroups };
  }
  const baseGroups = currentGroups ?? {};
  const cleanedGroups = sanitizeGroupsForMove(move, baseGroups, archetypeId);
  const changed =
    cleanedGroups !== baseGroups || (!currentGroups && Object.keys(cleanedGroups).length > 0);
  const groups = Object.keys(cleanedGroups).length > 0 ? cleanedGroups : undefined;
  // Se l'entry esisteva ma il sanitize l'ha svuotata, è un cambio.
  const removalChange = !groups && currentGroups !== undefined;
  return { changed: changed || removalChange, groups };
}

function sanitizeGroupsForMove(
  move: Move,
  groups: Record<string, string[]>,
  archetypeId: string | undefined,
): Record<string, string[]> {
  const moveGroups = getMoveChoiceGroups(move);
  const groupsById = new Map<string, ChoiceGroup>(moveGroups.map((g) => [g.id, g]));
  let groupChanged = false;
  const result: Record<string, string[]> = {};
  // (1) Itera sui gruppi del catalogo (anche se non in state): permette
  // l'auto-add delle opzioni free la prima volta che il gruppo viene
  // toccato dall'archetipo.
  for (const catalogGroup of moveGroups) {
    const currentSelections = groups[catalogGroup.id] ?? [];
    const sanitized = sanitizeGroupSelection(catalogGroup, currentSelections, archetypeId);
    if (sanitized !== currentSelections) groupChanged = true;
    if (sanitized.length > 0) result[catalogGroup.id] = sanitized;
  }
  // (2) Preserva entry nel state per gruppi non in catalogo (safety, edge
  // case: schema cambiato senza migrazione del draft).
  for (const [gid, sel] of Object.entries(groups)) {
    if (!groupsById.has(gid)) {
      result[gid] = sel;
    }
  }
  return groupChanged ? result : groups;
}

function sanitizeGroupSelection(
  group: ChoiceGroup,
  selectedIds: readonly string[],
  archetypeId: string | undefined,
): string[] {
  const optionsById = new Map(group.options.map((o) => [o.id, o]));
  // (1) filtra opzioni non più disponibili (requiresArchetype invalido)
  const filtered = selectedIds.filter((id) => {
    const opt = optionsById.get(id);
    if (!opt) return true; // opzione sconosciuta: mantieni per safety
    return isMoveChoiceAvailable(opt, archetypeId);
  });
  // (2) auto-add opzioni free per l'archetipo corrente
  const freeOptionIds = group.options
    .filter((o) => isMoveChoiceFree(o, archetypeId))
    .map((o) => o.id);
  const final = [...filtered];
  for (const fid of freeOptionIds) {
    if (!final.includes(fid)) final.push(fid);
  }
  // Idempotenza: stessa lunghezza + stessi ID nello stesso ordine → no change
  if (final.length === selectedIds.length && final.every((id, i) => id === selectedIds[i])) {
    return selectedIds as string[];
  }
  return final;
}
